import clsx from "clsx";
import _throttle from "lodash/throttle";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import AdsenseShopping from "web/react/components/adsense-shopping/adsense-shopping";
import BrandAdLoader from "web/react/components/brand-ad-loader/brand-ad-loader";
import { BackToTopButton } from "web/react/components/buttons/back-to-top-button/back-to-top-button";
import { CuratedAds } from "web/react/components/curated-ads/curated-ads";
import FeedCountAndSort from "web/react/components/feed-count-and-sort/feed-count-and-sort";
import { FeedGrid } from "web/react/components/feed-grid/feed-grid";
import { FeedHeader } from "web/react/components/feed-header/feed-header";
import { FilterContainerEmo } from "web/react/components/filters-emo/filter-container-emo";
import { SuggestedPills } from "web/react/components/filters-emo/suggested-pills";
import { MembershipPromoOverlay } from "web/react/components/membership-promo-overlay";
import { MoreLikeThisRail } from "web/react/components/more-like-this-rail/more-like-this-rail";
import { ProductOverlay } from "web/react/components/product-overlay";
import { RecentlyViewed } from "web/react/components/recently-viewed/recently-viewed";
import { RelatedProductsOverlay } from "web/react/components/related-products-overlay";
import { RelatedRail } from "web/react/components/related-rail/related-rail";
import { RelatedSearchesRail } from "web/react/components/related-searches-rail/related-searches-rail";
import { SaveSearchButton } from "web/react/components/save-search-button/save-search-button";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { setFeedBreadcrumbs } from "web/redux/ducks/feed-breadcrumbs";
import store, { ReduxStoreState } from "web/redux/store";
import environment from "web/script/modules/environment";
import { languageIsEnglish } from "web/script/modules/language";
import userProfiler from "web/script/modules/userprofiler";
import {
    AdsenseForShoppingSerializer,
    CuratedAdsSerializer,
    FeedHeaderSerializer,
    FeedNavigationSerializer,
    ProductFeedSerializer,
    RecentlyViewedSerializer,
    RelatedLinksSerializer,
    RelatedRailSerializer,
} from "web/types/serializers";
import { UniversalFiltersData } from "web/types/universal-filters";
import { FeedContextProvider } from "./feed.context";
import * as styles from "./feed.css";
import { FeedPage, getFeedSubTitle, getFeedTitle } from "./utils";

interface FeedProps {
    header: FeedHeaderSerializer;
    universalFilters: UniversalFiltersData;
    productFeed: ProductFeedSerializer;
    adsenseForShopping: AdsenseForShoppingSerializer;
    feedNavigation: FeedNavigationSerializer;
    relatedSearchesRail: RelatedLinksSerializer;
    relatedDesignersRail: RelatedRailSerializer;
    relatedRetailersRail: RelatedRailSerializer;
    relatedStoreBrandsRail: RelatedRailSerializer;
    recentlyViewedRail: RecentlyViewedSerializer;
    curatedAds: CuratedAdsSerializer;
}

export function Feed({
    header,
    universalFilters,
    productFeed,
    adsenseForShopping,
    curatedAds,
    feedNavigation,
    relatedSearchesRail,
    relatedDesignersRail,
    relatedRetailersRail,
    relatedStoreBrandsRail,
    recentlyViewedRail,
}: FeedProps): React.ReactElement {
    const overlayRef = useRef(null);

    const feedPageType = environment.get("feedType") as FeedPage;
    const feedType = productFeed.type || "";
    const { isLoggedIn } = userProfiler;
    const blurFeed = feedPageType === FeedPage.WISH_LIST && !isLoggedIn();
    const { isMobileViewport } = useDomViewport();

    useEffect(() => {
        store.dispatch(setFeedBreadcrumbs(feedNavigation?.shop_breadcrumbs));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const feedCountState = useSelector((state: ReduxStoreState) => state.feedCount);
    const productCount = feedCountState.product_count ?? productFeed.feed_count?.product_count ?? 0;

    const retailerCount =
        feedCountState.retailer_count ?? productFeed.feed_count?.retailer_count ?? 0;

    const feedHeaderState = useSelector((state: ReduxStoreState) => state.feedHeader);
    const feedHeader = feedHeaderState.gender ? feedHeaderState : header;

    const moreLikeThisState = useSelector((state: ReduxStoreState) => state.feedMoreLikeThis);
    const moreLikeThisProducts =
        moreLikeThisState.feed_cards.length > 0
            ? moreLikeThisState.feed_cards
            : productFeed.more_like_this_rail?.feed_cards;

    const mainTitle = getFeedTitle(feedPageType, feedHeader, universalFilters);
    const subtitle = getFeedSubTitle(feedPageType);
    const actions =
        (feedPageType === FeedPage.SEARCH && languageIsEnglish() && (
            <SaveSearchButton
                term={feedHeader.title ?? ""}
                gender={feedHeader.gender}
                next={feedHeader.search_next ?? ""}
            />
        )) ||
        undefined;

    const shortDescription: string | null =
        feedPageType !== FeedPage.RELATED_PRODUCTS ? feedHeader.short_description : null;
    const description: string | null =
        feedPageType !== FeedPage.RELATED_PRODUCTS ? feedHeader.description : null;

    const seoMoreLikeThisInProductCards = environment.getFeature(
        "seo_more_like_this_in_product_cards"
    );

    const shouldEnableSuggestedPills =
        environment.getFeature("act_popular_pills_designer_type_feed") ||
        environment.getFeature("act_popular_pills_designer_category_feed") ||
        environment.getFeature("act_popular_pills_designer_profile_feed");

    return (
        <FeedContextProvider>
            <div ref={overlayRef}>
                <ProductOverlay parentRef={overlayRef} pageType="feed" />
            </div>

            {!!seoMoreLikeThisInProductCards && seoMoreLikeThisInProductCards !== "control" && (
                <RelatedProductsOverlay />
            )}

            {/* Note: We're still using the `header` from the server and not redux */}
            {header?.brand_ad && (
                <div className={styles.brandAd}>
                    <div className={styles.brandAdGridWrapper}>
                        <BrandAdLoader {...header?.brand_ad} />
                    </div>
                </div>
            )}

            <div className={styles.feed}>
                <div className={clsx(styles.feedGrid, { [styles.blurFeed]: blurFeed })}>
                    <FeedHeader
                        title={mainTitle}
                        subtitle={subtitle}
                        profile={feedHeader}
                        actions={actions}
                        shortDescription={shortDescription}
                        description={description}
                        type={feedPageType}
                        productCount={productCount}
                        retailerCount={retailerCount}
                        breadcrumbs={feedNavigation?.shop_breadcrumbs}
                    />
                </div>
            </div>
            <FilterBar
                universalFilters={universalFilters}
                productCount={productCount}
                retailerCount={retailerCount}
                productFeed={productFeed}
                blurFeed={blurFeed}
            />
            <div className={styles.feed}>
                {blurFeed && (
                    <View
                        width="full"
                        height="full"
                        marginBottom="xxxl"
                        className={styles.feedGrid}
                    >
                        <MembershipPromoOverlay />
                    </View>
                )}

                <div className={clsx(styles.feedGrid, { hidden: blurFeed })}>
                    {shouldEnableSuggestedPills && <SuggestedPills filters={universalFilters} />}

                    <FeedGrid
                        data={productFeed}
                        description={description}
                        feedType={feedPageType}
                        inFeedBrandAd={productFeed?.in_feed_brand_ad}
                    />

                    <div className={styles.feedExtraContent}>
                        {curatedAds && <CuratedAds {...curatedAds} feedType={feedType} />}
                        {adsenseForShopping && <AdsenseShopping {...adsenseForShopping} />}
                        {moreLikeThisProducts && moreLikeThisProducts.length > 0 && (
                            <MoreLikeThisRail products={moreLikeThisProducts} feedType={feedType} />
                        )}
                        {recentlyViewedRail && (
                            <RecentlyViewed
                                feedType={feedType}
                                title={recentlyViewedRail.title}
                                current_product_id={recentlyViewedRail.current_product_id}
                                in_overlay={recentlyViewedRail.in_overlay}
                                products={recentlyViewedRail.products}
                                is_loading={recentlyViewedRail.is_loading}
                            />
                        )}
                        {relatedSearchesRail?.links.length > 0 && (
                            <RelatedSearchesRail
                                relatedSearches={relatedSearchesRail.links}
                                title={relatedSearchesRail.title}
                            />
                        )}
                        {relatedDesignersRail?.candidates && (
                            <RelatedRail
                                title={relatedDesignersRail.title}
                                candidates={relatedDesignersRail.candidates}
                                analyticsLabel={"designers"}
                            />
                        )}
                        {relatedStoreBrandsRail?.candidates && (
                            <RelatedRail
                                title={relatedStoreBrandsRail.title}
                                candidates={relatedStoreBrandsRail.candidates}
                                analyticsLabel={"designers"}
                            />
                        )}
                        {relatedRetailersRail?.candidates && (
                            <RelatedRail
                                title={relatedRetailersRail.title}
                                candidates={relatedRetailersRail.candidates}
                                analyticsLabel={"retailers"}
                            />
                        )}
                    </div>
                </div>
            </div>
            {isMobileViewport && <BackToTopButton />}
        </FeedContextProvider>
    );
}

function FilterBar({
    universalFilters,
    productCount,
    retailerCount,
    productFeed,
    blurFeed,
}): React.ReactElement {
    // ENRICH-3289
    // Stick the filters at the top of the viewport
    // `control` - no sticky
    // `sticky` - stick to the top of the viewport
    // `onscroll` - stick to the top but show filters only on scroll up
    const [scroll, setScroll] = useState("");
    const [isSticky, setIsSticky] = useState(false);
    const [prevScrollTop, setPrevScrollTop] = useState(0);
    const filtersRef = useRef<HTMLDivElement>(null);
    const filtersBarOffsetTop = useRef(0);

    const stickyBarFlag = String(environment.getFeature("act_sticky_feed_filters"));

    useEffect(() => {
        if (stickyBarFlag !== "onscroll") {
            return;
        }

        if (!filtersBarOffsetTop.current) {
            filtersBarOffsetTop.current = filtersRef.current?.offsetTop || 0;
        }

        function onWindowScroll(): void {
            const scrollTop = window.scrollY;

            if (scrollTop < prevScrollTop) {
                setScroll("up");
            } else {
                setScroll("down");
            }

            setPrevScrollTop(scrollTop);
            setIsSticky(filtersBarOffsetTop.current <= scrollTop);
        }

        const throttledOnWindowScroll = _throttle(onWindowScroll, 100);

        window.addEventListener("scroll", throttledOnWindowScroll);

        return () => {
            window.removeEventListener("scroll", throttledOnWindowScroll);
        };
    }, [stickyBarFlag, prevScrollTop]);

    return (
        <div
            ref={filtersRef}
            className={clsx({
                [styles.filtersSticky]: ["sticky", "onscroll"].includes(stickyBarFlag),
                [styles.filtersStickyHidden]:
                    stickyBarFlag === "onscroll" && scroll === "down" && isSticky,
            })}
        >
            <div className={clsx(styles.feed)}>
                <div className={clsx(styles.feedGrid, { [styles.blurFeed]: blurFeed })}>
                    <FilterContainerEmo
                        filters={universalFilters}
                        productCount={productCount}
                        sortOptions={productFeed.feed_sort_dropdown.sort_options}
                        shouldCloseDropdown={
                            stickyBarFlag === "onscroll" && scroll === "down" && isSticky
                        }
                    />

                    <FeedCountAndSort
                        productCount={productCount}
                        retailerCount={retailerCount}
                        sortOptions={productFeed.feed_sort_dropdown.sort_options}
                    />
                </div>
            </div>
        </div>
    );
}
